import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { util as sdkUtil } from '../util/sdkLoader';
import { denormalisedResponseEntities, ensureOwnListing } from '../util/data';
import * as log from '../util/log';
import { LISTING_STATE_DRAFT } from '../util/types';
import { storableError } from '../util/errors';
import { isUserAuthorized } from '../util/userHelpers';
import {
  INQUIRY_STATE_NAMES,
  isViewerMove,
  getMoveStateNames,
  fetchLastMessagesForCappedInquiries,
} from '../util/inboxAttention';

import { authInfo } from './auth.duck';
import { updateStripeConnectAccount } from './stripeConnectAccount.duck';

// ================ Helper Functions ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
    ? newCurrentUser
    : { id, type, attributes, ...oldRelationships, ...relationships };
};

// ================ Async Thunks ================ //

//////////////////////////////////////////////////////////////////////
// Fetch ownListings to check if currentUser has published listings //
//////////////////////////////////////////////////////////////////////

const fetchCurrentUserHasListingsPayloadCreator = (options, thunkAPI) => {
  const { getState, extra: sdk, rejectWithValue } = thunkAPI;
  // fetchCurrentUser passes the id: its result is not in the store yet at that point (#47).
  const currentUserId = options?.currentUserId || getState().user.currentUser?.id;

  if (!currentUserId) {
    return Promise.resolve({ hasListings: false });
  }

  const params = {
    // Since we are only interested in if the user has published
    // listings, we only need at most one result.
    states: 'published',
    page: 1,
    perPage: 1,
  };

  return sdk.ownListings
    .query(params)
    .then(response => {
      const hasListings = response.data.data && response.data.data.length > 0;

      const hasPublishedListings =
        hasListings &&
        ensureOwnListing(response.data.data[0]).attributes.state !== LISTING_STATE_DRAFT;
      return { hasListings: !!hasPublishedListings };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserHasListingsThunk = createAsyncThunk(
  'user/fetchCurrentUserHasListings',
  fetchCurrentUserHasListingsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserHasListings = options => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserHasListingsThunk(options)).unwrap();
};

///////////////////////////////////////////////////////////
// Fetch transactions to check if currentUser has orders //
///////////////////////////////////////////////////////////

const fetchCurrentUserHasOrdersPayloadCreator = (
  options,
  { getState, extra: sdk, rejectWithValue }
) => {
  // fetchCurrentUser passes the id: its result is not in the store yet at that point (#47).
  if (!(options?.currentUserId || getState().user.currentUser)) {
    return Promise.resolve({ hasOrders: false });
  }

  const params = {
    only: 'order',
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const hasOrders = response.data.data && response.data.data.length > 0;
      return { hasOrders: !!hasOrders };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserHasOrdersThunk = createAsyncThunk(
  'user/fetchCurrentUserHasOrders',
  fetchCurrentUserHasOrdersPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserHasOrders = options => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserHasOrdersThunk(options)).unwrap();
};

/////////////////////////////////////////////////////////////////////////////////////
// Fetch transactions in specific states to check if currentUser has notifications //
/////////////////////////////////////////////////////////////////////////////////////

// D37: the sale / order counts read the transactions themselves (an unavailable counterparty
// is nobody's move), so they count the most recently active 100, like the inquiry count.
const NOTIFICATION_COUNT_PAGE_SIZE = 100;

// D34: inquiries have no attention states — the turn is in the messages, which the API
// cannot filter on. One extra query fetches the inquiries with their messages' senders and
// counts the ones where it is the viewer's move (isViewerMove, the inbox's predicate).
const INQUIRY_COUNT_PAGE_SIZE = 100;

const fetchInquiryMoves = (sdk, viewerId) => {
  if (!viewerId) {
    return Promise.resolve({ saleInquiryMoves: 0, orderInquiryMoves: 0 });
  }
  return sdk.transactions
    .query({
      states: INQUIRY_STATE_NAMES.map(state => `state/${state}`).join(','),
      include: ['provider', 'customer', 'messages', 'messages.sender'],
      'fields.transaction': ['processName', 'lastTransition'],
      'fields.message': ['createdAt'],
      // D37: a deleted or banned counterparty makes it nobody's move
      'fields.user': ['profile.abbreviatedName', 'deleted', 'banned'],
      sort: 'lastMessageAt,lastTransitionedAt,createdAt',
      page: 1,
      perPage: INQUIRY_COUNT_PAGE_SIZE,
    })
    .then(response => {
      const transactions = denormalisedResponseEntities(response);
      return fetchLastMessagesForCappedInquiries(sdk, transactions).then(lastMessages => {
        const moves = transactions.filter(tx =>
          isViewerMove(tx, viewerId, { lastMessage: lastMessages[tx.id.uuid] })
        );
        const isProvider = tx => tx?.provider?.id?.uuid === viewerId.uuid;
        return {
          saleInquiryMoves: moves.filter(isProvider).length,
          orderInquiryMoves: moves.filter(tx => !isProvider(tx)).length,
        };
      });
    })
    .catch(e => {
      // The state-based counts still update if only this query fails.
      log.error(e, 'fetch-inquiry-moves-failed');
      return { saleInquiryMoves: 0, orderInquiryMoves: 0 };
    });
};

const fetchCurrentUserNotificationsPayloadCreator = (
  options,
  { extra: sdk, getState, rejectWithValue }
) => {
  // fetchCurrentUser passes the id: its own result is not in the store yet at that point.
  const viewerId = options?.currentUserId || getState()?.user?.currentUser?.id;
  // D37: the states where the viewer's side can have a move, counted with the inbox's own
  // resolver (isViewerMove), which needs the parties' deleted / banned flags.
  const moveCountParams = txRole => ({
    states: getMoveStateNames(txRole)
      .map(state => `state/${state}`)
      .join(','),
    include: ['provider', 'customer'],
    'fields.transaction': ['processName', 'lastTransition'],
    'fields.user': ['deleted', 'banned'],
    page: 1,
    perPage: NOTIFICATION_COUNT_PAGE_SIZE,
  });
  const paramsForSales = { only: 'sale', ...moveCountParams('provider') };
  const paramsForOrders = { only: 'order', ...moveCountParams('customer') };
  const countMoves = response =>
    denormalisedResponseEntities(response).filter(tx => isViewerMove(tx, viewerId)).length;

  return Promise.all([
    sdk.transactions.query(paramsForSales),
    sdk.transactions.query(paramsForOrders),
    fetchInquiryMoves(sdk, viewerId),
  ])
    .then(([sales, orders, inquiryMoves]) => {
      const saleNotificationsCount = countMoves(sales) + inquiryMoves.saleInquiryMoves;
      const orderNotificationsCount = countMoves(orders) + inquiryMoves.orderInquiryMoves;
      return { saleNotificationsCount, orderNotificationsCount };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserNotificationsThunk = createAsyncThunk(
  'user/fetchCurrentUserNotifications',
  fetchCurrentUserNotificationsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserNotifications = options => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserNotificationsThunk(options)).unwrap();
};

// The current_user/show request in flight (default parameters only). A page's loadData (ListingPage,
// ProfilePage, InboxPage, …) fetches the user while the post-hydration fetch of src/index.js is still
// running; the second caller gets the same request instead of a duplicate (backlog #4, H1-a). The
// one-second timestamp rule below covers the sequential case only.
let currentUserShowInFlight = null;

const fetchCurrentUserPayloadCreator = (options, thunkAPI) => {
  const { getState, dispatch, extra: sdk, rejectWithValue } = thunkAPI;
  const state = getState();
  const { currentUserHasListings, currentUserShowTimestamp } = state.user || {};
  const { isAuthenticated } = state.auth;
  const {
    callParams = null,
    updateHasListings = true,
    updateNotifications = true,
    afterLogin,
    enforce = false, // Automatic emailVerification might be called too fast
  } = options || {};

  // Double fetch might happen when e.g. profile page is making a full page load
  const aSecondAgo = new Date().getTime() - 1000;
  if (!enforce && currentUserShowTimestamp > aSecondAgo) {
    return Promise.resolve(state.user.currentUser);
  }

  if (!isAuthenticated && !afterLogin) {
    // Make sure current user is null
    return Promise.resolve(null);
  }

  if (!enforce && !callParams && currentUserShowInFlight) {
    return currentUserShowInFlight;
  }

  const parameters = callParams || {
    include: ['effectivePermissionSet', 'profileImage', 'stripeAccount'],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
    ],
    'imageVariant.square-xsmall': sdkUtil.objectQueryString({
      w: 40,
      h: 40,
      fit: 'crop',
    }),
    'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
      w: 80,
      h: 80,
      fit: 'crop',
    }),
  };

  const request = sdk.currentUser
    .show(parameters)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      const currentUser = entities[0];

      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(updateStripeConnectAccount(currentUser.stripeAccount));
      }

      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);
      return currentUser;
    })
    .then(currentUser => {
      // If currentUser is not active (e.g. in 'pending-approval' state),
      // then they don't have listings or transactions that we care about.
      if (isUserAuthorized(currentUser)) {
        if (currentUserHasListings === false && updateHasListings !== false) {
          dispatch(fetchCurrentUserHasListingsThunk({ currentUserId: currentUser.id }));
        }

        if (updateNotifications !== false) {
          dispatch(fetchCurrentUserNotifications({ currentUserId: currentUser.id }));
        }

        if (!currentUser.attributes.emailVerified) {
          dispatch(fetchCurrentUserHasOrdersThunk({ currentUserId: currentUser.id }));
        }
      }

      // Make sure auth info is up to date
      dispatch(authInfo());
      return currentUser;
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      log.error(e, 'fetch-current-user-failed');
      return rejectWithValue(storableError(e));
    });

  if (!callParams) {
    currentUserShowInFlight = request;
    request.finally(() => {
      currentUserShowInFlight = null;
    });
  }
  return request;
};

export const fetchCurrentUserThunk = createAsyncThunk(
  'user/fetchCurrentUser',
  fetchCurrentUserPayloadCreator
);
// Backward compatible wrapper for the thunk
/**
 * Fetch currentUser API entity.
 *
 * @param {Object} options
 * @param {Object} [options.callParams]           Optional parameters for the currentUser.show().
 * @param {boolean} [options.updateHasListings]   Make extra call for fetchCurrentUserHasListings()?
 * @param {boolean} [options.updateNotifications] Make extra call for fetchCurrentUserNotifications()?
 * @param {boolean} [options.afterLogin]          Fetch is no-op for unauthenticated users except after login() call
 * @param {boolean} [options.enforce]             Enforce the call even if the currentUser entity is freshly fetched.
 */
export const fetchCurrentUser = options => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserThunk(options)).unwrap();
};

/////////////////////////////////////////////
// Send verification email to currentUser //
/////////////////////////////////////////////

const sendVerificationEmailPayloadCreator = (_, { extra: sdk, rejectWithValue }) => {
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => ({}))
    .catch(e => rejectWithValue(storableError(e)));
};
export const sendVerificationEmailThunk = createAsyncThunk(
  'user/sendVerificationEmail',
  sendVerificationEmailPayloadCreator,
  {
    condition: (_, { getState }) => {
      return !getState()?.user?.sendVerificationEmailInProgress;
    },
  }
);

// Backward compatible wrapper for the thunk
export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  return dispatch(sendVerificationEmailThunk()).unwrap();
};

// ================ Slice ================ //

const userSlice = createSlice({
  name: 'user',
  initialState: {
    currentUser: null,
    currentUserShowTimestamp: 0,
    currentUserShowError: null,
    currentUserHasListings: false,
    // The has-listings query is on its way (D51: the /inbox notice waits for the answer, so it never
    // pops in above rows already on screen)
    currentUserHasListingsInProgress: false,
    currentUserHasListingsError: null,
    currentUserSaleNotificationCount: 0,
    currentUserOrderNotificationCount: 0,
    currentUserNotificationCountError: null,
    currentUserHasOrders: null, // This is not fetched unless unverified emails exist
    currentUserHasOrdersError: null,
    sendVerificationEmailInProgress: false,
    sendVerificationEmailError: null,
  },
  reducers: {
    clearCurrentUser: state => {
      state.currentUser = null;
      state.currentUserShowError = null;
      state.currentUserHasListings = false;
      state.currentUserHasListingsError = null;
      state.currentUserSaleNotificationCount = 0;
      state.currentUserOrderNotificationCount = 0;

      state.currentUserNotificationCountError = null;
    },
    setCurrentUser: (state, action) => {
      state.currentUser = mergeCurrentUser(state.currentUser, action.payload);
    },
    setCurrentUserHasOrders: state => {
      state.currentUserHasOrders = true;
    },
  },
  extraReducers: builder => {
    builder
      // fetchCurrentUser
      .addCase(fetchCurrentUserThunk.pending, state => {
        state.currentUserShowError = null;
      })
      .addCase(fetchCurrentUserThunk.fulfilled, (state, action) => {
        state.currentUser = mergeCurrentUser(state.currentUser, action.payload);
        state.currentUserShowTimestamp = action.payload ? new Date().getTime() : 0;
      })
      .addCase(fetchCurrentUserThunk.rejected, (state, action) => {
        // eslint-disable-next-line no-console
        console.error(action.payload);
        state.currentUserShowError = action.payload;
      })
      // fetchCurrentUserHasListings
      .addCase(fetchCurrentUserHasListingsThunk.pending, state => {
        state.currentUserHasListingsInProgress = true;
        state.currentUserHasListingsError = null;
      })
      .addCase(fetchCurrentUserHasListingsThunk.fulfilled, (state, action) => {
        state.currentUserHasListingsInProgress = false;
        state.currentUserHasListings = action.payload.hasListings;
      })
      .addCase(fetchCurrentUserHasListingsThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line
        state.currentUserHasListingsInProgress = false;
        state.currentUserHasListingsError = action.payload;
      })
      // fetchCurrentUserNotifications
      .addCase(fetchCurrentUserNotificationsThunk.pending, state => {
        state.currentUserNotificationCountError = null;
      })
      .addCase(fetchCurrentUserNotificationsThunk.fulfilled, (state, action) => {
        state.currentUserSaleNotificationCount = action.payload.saleNotificationsCount;
        state.currentUserOrderNotificationCount = action.payload.orderNotificationsCount;
      })
      .addCase(fetchCurrentUserNotificationsThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line
        state.currentUserNotificationCountError = action.payload;
      })
      // fetchCurrentUserHasOrders
      .addCase(fetchCurrentUserHasOrdersThunk.pending, state => {
        state.currentUserHasOrdersError = null;
      })
      .addCase(fetchCurrentUserHasOrdersThunk.fulfilled, (state, action) => {
        state.currentUserHasOrders = action.payload.hasOrders;
      })
      .addCase(fetchCurrentUserHasOrdersThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line
        state.currentUserHasOrdersError = action.payload;
      })
      // sendVerificationEmail
      .addCase(sendVerificationEmailThunk.pending, state => {
        state.sendVerificationEmailInProgress = true;
        state.sendVerificationEmailError = null;
      })
      .addCase(sendVerificationEmailThunk.fulfilled, state => {
        state.sendVerificationEmailInProgress = false;
      })
      .addCase(sendVerificationEmailThunk.rejected, (state, action) => {
        state.sendVerificationEmailInProgress = false;
        state.sendVerificationEmailError = action.payload;
      });
  },
});

export default userSlice.reducer;

export const { clearCurrentUser, setCurrentUser, setCurrentUserHasOrders } = userSlice.actions;

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};
